import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { AlertTriangle, PhoneCall, Check, X } from "lucide-react";

export default function CariGorevlerim() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteFor, setNoteFor] = useState(null); // müşteri adı
  const [note, setNote] = useState("");
  const [num, setNum] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/cari/gorevlerim").then((res) => setTasks(res.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return null;

  const bekleyen = tasks.filter((t) => !t.arandi_bugun);
  const toplamGecikmis = bekleyen.reduce((s, t) => s + (t.gecikmis_adet || 0), 0);
  const toplamBugun = bekleyen.reduce((s, t) => s + (t.bugun_adet || 0), 0);

  const openNote = (musteri) => { setNoteFor(musteri); setNote(""); setNum(""); };

  const submitNote = async () => {
    if (!note.trim()) { toast.error("Arama notu zorunludur — görev not olmadan tamamlanamaz"); return; }
    setSaving(true);
    try {
      await api.post("/cari/musteri-gorusme", {
        musteri: noteFor,
        notlar: note.trim(),
        aranan_numara: num.trim(),
        arama_tarihi: new Date().toISOString().slice(0, 10),
        durum: "gorusuldu",
      });
      toast.success(`${noteFor} arandı, not eklendi — görev tamamlandı`);
      setNoteFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Not eklenemedi");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm mb-6" data-testid="cari-gorevlerim">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E7DECC]">
        <PhoneCall className="w-4 h-4 text-[#A63A3A]" />
        <span className="font-heading font-bold text-sm tracking-tight">Cari Görevlerim (Aranacak Müşteriler)</span>
        <span className="ml-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#A63A3A]/10 text-[#A63A3A]">{toplamGecikmis} gecikmiş</span>
        {toplamBugun > 0 && <span className="px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]">{toplamBugun} bugün</span>}
      </div>
      {tasks.length === 0 ? (
        <div className="px-4 py-8 text-center text-[#8C8377] text-sm" data-testid="cari-gorevlerim-empty">
          Size atanmış, vadesi gelen/geçen müşteri yok. 🎉
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
                <th className="px-4 py-2.5 kicker text-left">Müşteri</th>
                <th className="px-4 py-2.5 kicker text-right">Gecikmiş Taksit</th>
                <th className="px-4 py-2.5 kicker text-left">En Çok Gecikme</th>
                <th className="px-4 py-2.5 kicker text-right">Bugün</th>
                <th className="px-4 py-2.5 kicker text-right">Toplam Kalan</th>
                <th className="px-4 py-2.5 kicker text-right">Görev</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr
                  key={t.musteri}
                  className={`border-b border-[#E7DECC] transition-colors duration-150 ${t.arandi_bugun ? "bg-[#2F6E4E]/5 opacity-70" : "hover:bg-[#EFE7D6]"}`}
                  data-testid={`cari-gorev-row-${t.musteri}`}
                >
                  <td className="px-4 py-2 font-medium cursor-pointer" onClick={() => navigate(`/cari-musteri/${encodeURIComponent(t.musteri)}`)}>{t.musteri}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{t.gecikmis_adet}</td>
                  <td className="px-4 py-2">
                    {t.en_cok_gecikme > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#A63A3A]/10 text-[#A63A3A] text-xs font-semibold">
                        <AlertTriangle className="w-3 h-3" /> {t.en_cok_gecikme} gün
                      </span>
                    ) : <span className="text-[#8C8377] text-xs">-</span>}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {t.bugun_adet > 0 ? <span className="px-2 py-0.5 rounded-sm bg-[#B8933F]/10 text-[#B8933F] text-xs font-semibold">{t.bugun_adet}</span> : "-"}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#A63A3A]">{fmt(t.toplam_kalan)}</td>
                  <td className="px-4 py-2 text-right">
                    {t.arandi_bugun ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#2F6E4E]/10 text-[#2F6E4E] text-xs font-semibold" data-testid={`cari-gorev-done-${t.musteri}`} title={t.son_arama_not || ""}>
                        <Check className="w-3.5 h-3.5" /> Bugün Arandı
                      </span>
                    ) : (
                      <button onClick={() => openNote(t.musteri)} data-testid={`cari-gorev-ara-${t.musteri}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#A63A3A] hover:bg-[#8f3030] text-white rounded-sm text-xs font-semibold transition-colors duration-150">
                        <PhoneCall className="w-3.5 h-3.5" /> Ara & Not Ekle
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {noteFor && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setNoteFor(null)} />
          <div className="relative bg-white rounded-sm border border-[#E7DECC] w-full max-w-md shadow-2xl" data-testid="cari-gorev-note-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7DECC]">
              <span className="font-heading font-bold text-sm">{noteFor} — Arama Notu</span>
              <button onClick={() => setNoteFor(null)} className="p-1 text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="kicker block mb-1">Aranan Numara (opsiyonel)</label>
                <input value={num} onChange={(e) => setNum(e.target.value)} data-testid="cari-gorev-note-num" placeholder="05xx xxx xx xx" className="w-full border border-[#E7DECC] rounded-sm px-3 py-2 text-sm outline-none focus:border-[#B8933F]" />
              </div>
              <div>
                <label className="kicker block mb-1">Arama Notu <span className="text-[#A63A3A]">*</span></label>
                <textarea autoFocus value={note} onChange={(e) => setNote(e.target.value)} data-testid="cari-gorev-note-text" rows={4} placeholder="Görüşme sonucu, verilen söz, tahsilat durumu…" className="w-full border border-[#E7DECC] rounded-sm px-3 py-2 text-sm outline-none focus:border-[#B8933F] resize-none" />
                <p className="text-xs text-[#8C8377] mt-1">Not yazılmadan görev tamamlanamaz.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#E7DECC]">
              <button onClick={() => setNoteFor(null)} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6]">İptal</button>
              <button onClick={submitNote} disabled={saving || !note.trim()} data-testid="cari-gorev-note-save" className="px-4 py-2 bg-[#A63A3A] hover:bg-[#8f3030] text-white rounded-sm text-sm font-medium disabled:opacity-50">{saving ? "Kaydediliyor…" : "Notu Kaydet & Tamamla"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
